import React, { memo, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Map, List } from 'immutable'
import { TopShelf } from '@aytm/app/swidget/questions/components/sh/topShelf'
import { ItemPrice } from '@aytm/app/swidget/questions/components/sh/price'
import { PALLET_SECTION_WIDTH } from '@aytm/lib/shelves/container'
import shelfStyle from '@aytm/app/swidget/questions/styles/sh/shelf.module.scss'

Prices.propTypes = {
  questionSchema: PropTypes.instanceOf(Map).isRequired,
  mobile: PropTypes.bool,
  first: PropTypes.bool,
  palletSectionIndex: PropTypes.number,
  palletSectionsCount: PropTypes.number,
  topShelf: PropTypes.bool,
  backupPack: PropTypes.bool,
  settings: PropTypes.any,
  config: PropTypes.instanceOf(List).isRequired,
  coordinates: PropTypes.instanceOf(List).isRequired,
  counts: PropTypes.instanceOf(List),
  shelvesMask: PropTypes.instanceOf(List).isRequired,
  answers: PropTypes.any.isRequired,
  width: PropTypes.number.isRequired
}

export function Prices(props) {
  const {
    questionSchema,
    backupPack,
    settings,
    mobile,
    width,
    answers,
    topShelf,
    first,
    palletSectionIndex = 0,
    palletSectionsCount = 1,
    shelvesMask,
    coordinates,
    counts,
    config
  } = props
  // pallet multi-section: width division uses sectionsCount; section offset comes from palletSectionIndex.
  const isPalletMulti = 'pallet' == questionSchema.get('shelf_type') && palletSectionsCount > 1
  const sectionsCount = isPalletMulti ? palletSectionsCount : questionSchema.get('double_width') ? 2 : 1
  const widthIsSplit = sectionsCount > 1
  // For pallet multi-section we mirror container.js: each section starts `palletSectionIndex × PALLET_SECTION_WIDTH`
  // to the right in global coords. Subtract that to translate to the price wrapper's local frame.
  // Non-pallet doubleWidth keeps the legacy `width` subtraction.
  const sectionXOffset = isPalletMulti
    ? palletSectionIndex * PALLET_SECTION_WIDTH
    : !first && questionSchema.get('double_width')
      ? width
      : 0
  return (
    <div
      className={
        `${shelfStyle.prices} ${shelfStyle[settings.styleClass]} ` +
        (topShelf ? `${shelfStyle.topShelved} ` : ``) +
        ('flex' == questionSchema.get('shelf_align') ? `${shelfStyle.flexed} ` : ``) +
        (widthIsSplit
          ? isPalletMulti
            ? palletSectionIndex === 0
              ? `${shelfStyle.leftShelves} `
              : // .second mirrors the shelf layer's -20px boundary-rail merge so both layers share one origin (OQ-WD-09)
                `${shelfStyle.rightShelves} ${shelfStyle.second} `
            : first
              ? `${shelfStyle.leftShelves} `
              : `${shelfStyle.rightShelves} `
          : ``)
      }
      data-testid="SW-SH-Prices"
      style={{
        width: widthIsSplit && !settings.fridge ? width / sectionsCount : width
      }}
    >
      {shelvesMask.map((shelf, i) => {
        let priceIndex = 0
        // pallet multi-section: shelvesMask rows are merged half-pairs while coordinate/count rows stay per
        // half-shelf — visual row i spans coordinate rows 2i (left half) and 2i+1 (right half) (OQ-WD-09).
        const shelfCoords = isPalletMulti
          ? (coordinates.get(2 * i) || List([])).concat(coordinates.get(2 * i + 1) || List([]))
          : coordinates.get(i)
        const shelfCounts = isPalletMulti
          ? ((counts && counts.get(2 * i)) || List([])).concat((counts && counts.get(2 * i + 1)) || List([]))
          : counts && counts.get(i)
        // pack → first coordinate of its block (facings × stacking entries per pack); findIndex by answer id
        // collapses onto the neighbouring unit when the same product fills adjacent packs.
        const packStart = pack =>
          shelf.slice(0, pack).reduce((acc, p, pi) => acc + p.get(1) * ((shelfCounts && shelfCounts.get(pi)) || 1), 0)
        return (
          <Fragment key={'shelf' + i}>
            {topShelf && (
              <TopShelf
                questionSchema={questionSchema}
                width={widthIsSplit ? width / sectionsCount + 10 : width}
                widthFactor={settings.widthFactor}
                mobile={mobile}
                shelf={shelf}
                answers={answers}
                prices={true}
              />
            )}
            <div
              className={
                `${shelfStyle.shelf} ${
                  shelfStyle['shelf' + (settings.double && settings.topShelf ? (i == shelvesMask.size - 1 ? 1 : 0) : i)]
                } ${shelfStyle[settings.styleClass]} ` +
                (i == questionSchema.get('answers_mask').size / sectionsCount - 1 ? `${shelfStyle.last} ` : ``) +
                (settings.customConfig &&
                settings.customConfig.getIn([i, 'shelf_type']) &&
                'pegboard' == settings.customConfig.getIn([i, 'shelf_type'])
                  ? `${shelfStyle['style' + settings.customConfig.getIn([i, 'shelf_type'])]} `
                  : ``) +
                (i &&
                settings.customConfig &&
                settings.customConfig.getIn([i - 1, 'shelf_type']) &&
                'pegboard' == settings.customConfig.getIn([i - 1, 'shelf_type'])
                  ? `${shelfStyle['prevStyle' + settings.customConfig.getIn([i - 1, 'shelf_type'])]} `
                  : ``)
              }
              style={{
                minHeight:
                  ((settings.customConfig ? settings.customConfig.getIn([i, 'height']) : settings.height) +
                    (i === shelvesMask.size - 1 ? settings.bottomAdditionalHeight || 0 : 0) +
                    (settings.customConfig && 'pegboard' == settings.customConfig.getIn([i, 'shelf_type'])
                      ? settings.pegAir
                      : questionSchema.get('version') > 0 && 'regular' == questionSchema.get('shelf_type')
                        ? 0
                        : settings.air) *
                      (backupPack ? 0 : 1)) *
                  settings.widthFactor,
                width: (widthIsSplit && !settings.fridge ? width / sectionsCount + 10 : width) - settings.walls + 1,
                marginLeft: 'counterFridge' == questionSchema.get('shelf_type') ? 0 * 50 : 0
              }}
            >
              <div
                className={`${shelfStyle.layer} ${
                  questionSchema.get('shelf_align') == 'center' ? shelfStyle.center : ''
                }`}
              >
                {!topShelf &&
                  Array.apply(null, Array(shelf.size)).map((_, packIndex) => {
                    const answer = answers.get(shelf.getIn([packIndex, 0]))
                    const nextAnswer = answers.get(shelf.getIn([packIndex + 1, 0]))
                    const itemsCount = shelf.getIn([packIndex, 1])

                    if (!shelfCoords || !shelfCoords.size) return ''

                    const coordIndex = isPalletMulti
                      ? packStart(packIndex)
                      : shelfCoords.findIndex((coord, ci) => answer.get('id') == coord.get(4) && ci >= packIndex)
                    const nextCoordIndex = nextAnswer
                      ? isPalletMulti
                        ? packStart(packIndex + 1)
                        : shelfCoords.findIndex((coord, ci) => nextAnswer.get('id') == coord.get(4) && ci >= packIndex)
                      : null
                    const coordX = coordIndex > -1 ? shelfCoords.getIn([coordIndex, 0]) : null
                    const nextCoordX = nextCoordIndex > -1 ? shelfCoords.getIn([nextCoordIndex, 0]) : null
                    const width = nextCoordX
                      ? (nextCoordX - coordX + answer.get('width') * itemsCount) / 2
                      : answer.get('width') * itemsCount

                    // exact pack centre from the first and last item coordinates — `coordX + w×items/2` assumes the
                    // item step equals the item width, which overshoots right when facings overlap (OQ-WD-09)
                    const lastItemX = isPalletMulti
                      ? shelfCoords.getIn(
                          [coordIndex + ((shelfCounts && shelfCounts.get(packIndex)) || 1) * (itemsCount - 1), 0],
                          coordX
                        )
                      : null

                    const priceLeft =
                      'flex' == questionSchema.get('shelf_align') ||
                      // ('group' == questionSchema.get('shelf_align') && itemsCount < 2) ||
                      'right' == questionSchema.get('shelf_align') ||
                      'left' == questionSchema.get('shelf_align') ||
                      (config && 'pegboard' == config.getIn([i, 'shelf_type']) && undefined != coordIndex)
                        ? shelfCoords.getIn([coordIndex, 0]) +
                          shelfCoords.getIn([coordIndex, 2]) / 2 -
                          20 -
                          sectionXOffset
                        : isPalletMulti
                          ? (coordX + lastItemX + shelfCoords.getIn([coordIndex, 2])) / 2 - 20 - sectionXOffset
                          : shelfCoords.getIn([coordIndex, 0]) +
                            (shelfCoords.getIn([coordIndex, 2]) * itemsCount) / 2 -
                            20 -
                            sectionXOffset

                    const localCoordinates = shelfCoords.reduce((acc, cur, ci) => {
                      if (0 == ci || (acc.last() && cur.get(0) != acc.last().get(0))) {
                        return acc.push(cur)
                      }
                      return acc
                    }, List([]))

                    const pegboardShelf = config && 'pegboard' == config.getIn([i, 'shelf_type'])
                    // a Rollback frame is wider than a facing, so a flex shelf draws it once per pack
                    const rollbackPack =
                      !pegboardShelf &&
                      'flex' == questionSchema.get('shelf_align') &&
                      'walmart' == settings.priceTemplate &&
                      answer.get('rollback')
                    const packFirst = priceIndex

                    return config && (pegboardShelf || 'flex' == questionSchema.get('shelf_align')) ? (
                      Array.apply(null, Array(itemsCount)).map((_, itemIndex) => {
                        priceIndex = priceIndex + 1
                        if (rollbackPack && itemIndex > 0) return null
                        return (
                          <div className={shelfStyle.items} key={'pack' + itemIndex} style={{ width: width }}>
                            <ItemPrice
                              key={'price' + itemIndex}
                              item={answer}
                              left={
                                rollbackPack
                                  ? (localCoordinates.getIn([packFirst, 0]) +
                                      localCoordinates.getIn([packFirst + itemsCount - 1, 0]) +
                                      localCoordinates.getIn([packFirst + itemsCount - 1, 2])) /
                                      2 -
                                    20
                                  : localCoordinates.getIn([priceIndex - 1, 0]) +
                                    localCoordinates.getIn([priceIndex - 1, 2]) / 2 -
                                    ('walmart' == questionSchema.get('store')
                                      ? 8
                                      : 'target' == questionSchema.get('store')
                                        ? 30
                                        : 22)
                              }
                              questionSchema={questionSchema}
                              type={settings.priceTemplate}
                              noRandom={'pegboard' == settings.customConfig.getIn([i, 'shelf_type'])}
                            />
                          </div>
                        )
                      })
                    ) : (
                      <div className={shelfStyle.items} key={'pack' + packIndex} style={{ width: width }}>
                        <ItemPrice
                          key={'price' + packIndex}
                          item={answer}
                          left={priceLeft}
                          questionSchema={questionSchema}
                          type={settings.priceTemplate}
                        />
                      </div>
                    )
                  })}
              </div>
            </div>
          </Fragment>
        )
      })}
    </div>
  )
}

export default memo(Prices, (__prevProps, __nextProps) => true)
